import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

interface Props {
  data: { categories: string[]; series: { name: string; data: (number | null)[] }[] };
  title?: string;
  neighborhoodName?: string;
  buildingCategoryLabel?: string;
  buildingIds?: number[];
  onBuildingClick?: (id: number) => void;
}

const FONT = "'Montserrat Light', 'Montserrat', sans-serif";
const FONT_SIZE = '11pt';
const FONT_STYLE = { fontFamily: FONT, fontSize: FONT_SIZE, fontWeight: 'normal' as const };

export const ShadowInventoryChart = React.forwardRef<HighchartsReact.RefObject, Props>(
  ({ data, title = 'Shadow Inventory', neighborhoodName = '', buildingCategoryLabel = '', buildingIds, onBuildingClick }, ref) => {
    const subtitle = [neighborhoodName, buildingCategoryLabel || null, 'Condominium Shadow Inventory']
      .filter(Boolean).join(' | ');

    const shadowLabelFormatter: Highcharts.DataLabelsFormatterCallbackFunction = function () {
      return this.y != null && this.y > 0 ? Math.round(this.y).toString() : '';
    };

    const options: Highcharts.Options = {
      chart: {
        type: 'column',
        height: 560,
        marginTop: 160,
        style: { fontFamily: FONT, fontSize: FONT_SIZE },
      },
      title: {
        text: title,
        align: 'left',
        style: { fontFamily: "'Montserrat', sans-serif", fontSize: '30pt', fontWeight: '500', color: '#000000' },
      },
      subtitle: {
        text: subtitle,
        align: 'left',
        style: { fontFamily: "'Montserrat', sans-serif", fontSize: '14pt', fontWeight: '500', color: '#66635F' },
      },
      xAxis: {
        categories: data.categories,
        title: { text: undefined },
        labels: {
          rotation: 0,
          style: { ...FONT_STYLE, fontSize: '9pt' },
          formatter: function () {
            let name = String(this.value);
            if (name.endsWith(', The')) name = 'The ' + name.slice(0, -5);
            const words = name.split(' ');
            const mid = Math.ceil(words.length / 2);
            const line1 = words.slice(0, mid).join(' ');
            const line2 = words.slice(mid).join(' ');
            return line2 ? `${line1}<br/>${line2}` : line1;
          },
        },
      },
      yAxis: {
        min: 0,
        title: { text: undefined },
        labels: { style: FONT_STYLE },
        stackLabels: { enabled: false },
        reversedStacks: false,
      },
      legend: {
        enabled: true,
        itemStyle: FONT_STYLE,
      },
      tooltip: {
        style: FONT_STYLE,
        formatter: function () {
          return `<b>${this.key}</b><br/><span style="color:${this.color}">●</span> ${this.series.name}: <b>${Math.round(this.y!)}</b>`;
        },
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          borderWidth: 0,
          dataLabels: { enabled: false },
          cursor: onBuildingClick ? 'pointer' : 'default',
          point: {
            events: {
              click: function () {
                if (onBuildingClick && buildingIds) {
                  const id = buildingIds[this.index];
                  if (id) onBuildingClick(id);
                }
              },
            },
          },
        },
      },
      series: data.series.map(s => {
        const isShadow = s.name === 'Shadow Inventory';
        return {
          type: 'column' as const,
          name: s.name,
          data: s.data,
          color: isShadow ? '#BDBDBD' : '#738FAF',
          dataLabels: isShadow
            ? {
                enabled: true,
                align: 'center' as const,
                verticalAlign: 'middle' as const,
                style: {
                  fontFamily: FONT,
                  fontSize: '9pt',
                  fontWeight: 'normal',
                  textOutline: 'none',
                  color: '#555555',
                },
                formatter: shadowLabelFormatter,
              }
            : { enabled: false },
        };
      }),
      credits: { enabled: false },
    };

    return <HighchartsReact ref={ref} highcharts={Highcharts} options={options} />;
  }
);
